import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  TableBody,
  IconButton,
  Alert,
} from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { Link } from "react-router-dom";
import { getSearchLink } from "./SavedSearchesPage";
import { useLanguage } from "../../Language";
import { DynamiResourceListView } from "./searches/SavedSearches";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteIcon from "@mui/icons-material/Delete";

export interface SavedSearchesDesktopGridProps {
  selected: DynamiResourceListView[];
  displayedSearches: DynamiResourceListView[];
  selectedRows: number[];
  total: number;
  handleSelectAllClick: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void>;
  handleClick: (
    event: React.MouseEvent<unknown, MouseEvent>,
    row: DynamiResourceListView,
  ) => void;
  setListToRename: (
    value: React.SetStateAction<DynamiResourceListView | null>,
  ) => void;
  setListToDelete: React.Dispatch<
    React.SetStateAction<DynamiResourceListView | null>
  >;
  translateFacetName: (facetName: string) => string;
}

export const SavedSearchesDesktopGrid: React.FC<
  SavedSearchesDesktopGridProps
> = ({
  selected,
  displayedSearches,
  selectedRows,
  total,
  handleSelectAllClick,
  handleClick,
  setListToRename,
  setListToDelete,
  translateFacetName,
}) => {
  const { translate } = useLanguage();
  return (
    <TableContainer>
      {displayedSearches.length > 0 && (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" aria-label="select">
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selected.length > 0 && selected.length < total
                    }
                    checked={total > 0 && selected.length === total}
                    onChange={handleSelectAllClick}
                    slotProps={{
                      input: {
                        "aria-label": translate("selectAll"),
                      },
                    }}
                  />
                </TableCell>

                <TableCell>{translate("searchName")}</TableCell>
                <TableCell aria-label="actions"></TableCell>
                <TableCell>{translate("searchCriteria")}</TableCell>
                <TableCell>{translate("sortedBy")}</TableCell>
                <TableCell>{translate("date")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedSearches.map((search, index) => {
                const isItemSelected = selectedRows.indexOf(search.id) !== -1;
                const labelId = `table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    onClick={(event) => handleClick(event, search)}
                    selected={isItemSelected}
                    key={search.id}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClick(e, search);
                        }}
                        slotProps={{
                          input: {
                            "aria-labelledby": labelId,
                          },
                        }}
                      />
                    </TableCell>

                    <TableCell id={labelId} scope="row">
                      <Link
                        to={getSearchLink(search)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {search.name}
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: {
                          md: "normal",
                          lg: "nowrap",
                        },
                      }}
                    >
                      <IconButton
                        aria-label={translate("rename")}
                        onClick={(event) => {
                          event.stopPropagation(); // prevent row click
                          setListToRename(search);
                        }}
                      >
                        <DriveFileRenameOutlineIcon />
                      </IconButton>
                      <IconButton
                        component={"a"}
                        target="_blank"
                        href={`/list/dynamic/${search.id}/rss`}
                        aria-label="Go to {resourceUtil.getSavedSearchExpression} rss feed"
                      >
                        <RssFeedIcon />
                      </IconButton>
                      <IconButton
                        aria-label={translate("remove")}
                        onClick={(event) => {
                          event.stopPropagation(); // prevent row click
                          setListToDelete(search);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "normal", flexShrink: 1 }}>
                      {translate("searchQuery")}
                      {": "}
                      {search.advanced
                        ? `(${translate("advancedSearch")})`
                        : search.expression}
                      {search.facetFilters.length > 0 ? ", " : ""}
                      {(search.facetFilters || []).map((filter, index) => (
                        <React.Fragment key={filter.id}>
                          {translateFacetName(filter.facetName)}
                          {": "}
                          {filter.facetValue}
                          {index < search.facetFilters.length - 1 && ", "}
                        </React.Fragment>
                      ))}
                    </TableCell>
                    <TableCell>{translate(search.sortType)}</TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: {
                          xs: "nowrap",
                          md: "normal",
                          lg: "nowrap",
                        },
                        flexShrink: 0,
                      }}
                    >
                      {dayjs(search.creationDate).format("MM-DD-YYYY")}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}
      {displayedSearches.length === 0 && (
        <Alert severity="info">{translate("noSavedSearchesMessage")}</Alert>
      )}
    </TableContainer>
  );
};
